import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderVersionSeasonData } from 'gql/graphql';
import { Loader } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';

import { useOrderSummaryTableColumns } from '../../WorkOrder/WorkOrderSummaryTableNew/hooks';
import { uerWorkOrderVersionTotals } from '../workOrderVersionQueries';

import Style from './WorkOrderVersionSummaryTable.module.css';

interface IWorkOrderVersionSummaryTable {
    costRounding: number;
    seasons: WorkOrderVersionSeasonData[];
    workOrderVersionId: number;
}

export const WorkOrderVersionSummaryTable = (props: IWorkOrderVersionSummaryTable) => {
    const { costRounding, seasons, workOrderVersionId } = props;

    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);

    const variables = useMemo(() => ({ workOrderVersionId }), [workOrderVersionId]);

    const [workOrderVersionResultData, workOrderResultStatus] = useRequest({
        requestId: 'uerWorkOrderVersionTotals',
        query: uerWorkOrderVersionTotals,
        variables,
        withEffect: Boolean(workOrderVersionId),
    });

    const workOrderResult = workOrderVersionResultData?.uerWorkOrderVersionTotals?.data || [];

    const isLoading = workOrderResultStatus === RequestStatus.FETCHING;

    const { fullSetSettingsColumns, isColumnVisible, columns, tableSettingsConfirm } = useOrderSummaryTableColumns({
        instanceId: String(workOrderVersionId),
        costRounding,
        workOrderSeasons: seasons,
    });

    const totalItems = useMemo(
        () => workOrderVersionResultData?.uerWorkOrderVersionTotals?.data?.length || 0,
        [workOrderVersionResultData?.uerWorkOrderVersionTotals?.data?.length],
    );

    return (
        <div className={Style.workOrderSummaryTable}>
            <TableTopPanel
                totalItems={totalItems}
                fetchedItems={totalItems}
                optionalColumns={fullSetSettingsColumns}
                isColumnVisible={isColumnVisible}
                handleTableSettingsConfirm={tableSettingsConfirm}
                tableApi={tableApi}
            />
            {isLoading && <Loader />}
            {!isLoading && (
                <div className="workOrderPrice">
                    <AgGridTable
                        ref={tableRef}
                        columnDefs={columns as any}
                        rowData={workOrderResult}
                        defaultColDef={{
                            sortable: false,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

WorkOrderVersionSummaryTable.displayName = 'WorkOrderVersionSummaryTable';
